import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import OffShiftBadge from "../../components/OffShiftBadge/OffShiftBadge";
import { getColorBaseTokenValue } from "../../design-tokens";

import ProductionApi, {
  SynopticResponse,
  AccumulatedProductionResponse,
  StateParetoResponse,
} from "../../services/ProductionApi";
import useAlerts from "../../hooks/useAlerts";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import AlertCard from "./AlertCard";
import DashCard from "./DashCard";
import styles from "./ReportDashboard.module.css";

const pad = (n: number): string => String(n).padStart(2, "0");
const toYmd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const ReportDashboard: React.FC = () => {
  const { t, i18n } = useTranslation("dashboardHome");
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  // Alertas em tempo real (hub SignalR). Sem pause nesta tela.
  const { alerts, hasReceived, receivedAt, now, status } = useAlerts(false);

  const [syn, setSyn] = useState<SynopticResponse | null>(null);
  const [acc, setAcc] = useState<AccumulatedProductionResponse | null>(null);
  const [par, setPar] = useState<StateParetoResponse | null>(null);
  const [kpiLoading, setKpiLoading] = useState<boolean>(true);

  const fmt = useCallback(
    (v: number, dec = 0): string =>
      Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }),
    [locale],
  );

  // KPIs (AC-5): reuso dos endpoints de produção. allSettled → uma fonte fora não derruba as outras.
  const loadKpis = useCallback(async (): Promise<void> => {
    const today = toYmd(new Date());
    const [synR, accR, parR] = await Promise.allSettled([
      ProductionApi.GetSynoptic(),
      ProductionApi.GetAccumulated(),
      ProductionApi.GetStatePareto({ from: today, to: today }),
    ]);
    if (synR.status === "fulfilled") setSyn((synR.value?.data as SynopticResponse) ?? null);
    if (accR.status === "fulfilled") setAcc((accR.value?.data as AccumulatedProductionResponse) ?? null);
    if (parR.status === "fulfilled") setPar((parR.value?.data as StateParetoResponse) ?? null);
    setKpiLoading(false);
  }, []);

  // Paint inicial dos KPIs.
  useEffect(() => {
    void loadKpis();
  }, [loadKpis]);
  // Atualização em tempo real via SignalR (substitui o polling de 30 s); alertas já vêm pelo hub.
  useLiveRefresh(() => void loadKpis());

  const accents = useMemo(
    () => ({
      station: getColorBaseTokenValue("color.secondary.700"),
      day: getColorBaseTokenValue("color.primary.500"),
      cycle: getColorBaseTokenValue("color.feedback.warning.500"),
      oee: getColorBaseTokenValue("color.secondary.500"),
      month: getColorBaseTokenValue("color.primary.700"),
      pareto: getColorBaseTokenValue("color.feedback.danger.500"),
    }),
    [],
  );

  const stateLabel = useCallback(
    (s: string): string =>
      ({
        Produzindo: t("stProduzindo"),
        CicloExcedido: t("stExcedido"),
        Ausencia: t("stAusencia"),
        FaltaMaterial: t("stMaterial"),
      })[s] ?? s,
    [t],
  );

  // Deriva o KPI "Maior perda hoje" do Pareto de hoje (maior tempo entre os estados não-produtivos).
  const paretoLoss = useMemo(() => {
    const states = (par?.states ?? []).filter((s) => s.state !== "Produzindo");
    if (!par?.hasData || states.length === 0) return null;
    const top = states.reduce((a, b) => (b.totalSeconds > a.totalSeconds ? b : a));
    const minutes = Math.round(top.totalSeconds / 60);
    if (minutes <= 0) return null; // perda < 30s arredonda p/ 0 → trata como "sem perdas"
    return { state: top.state, minutes };
  }, [par]);

  const live = status === "connected";

  const dailyAttain = syn && syn.daily.target > 0 ? (syn.daily.producedCount / syn.daily.target) * 100 : 0;
  const cycleMin = syn && syn.cycle.avgCycleSeconds != null ? syn.cycle.avgCycleSeconds / 60 : null;
  const stdMin = syn ? syn.cycle.standardCycleSeconds / 60 : 38.9;

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <OffShiftBadge />
        <span className={`${styles.livePill} ${live ? "" : styles.livePillOff}`}>
          <span className={`${styles.liveDot} ${live ? "" : styles.liveDotOff}`} />
          {live ? t("live") : t("offline")}
        </span>
      </PageHeader>

      {/* Alertas */}
      <section className={styles.alertsSection}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionTitle}>{t("alertsTitle")}</span>
          {alerts.length > 0 && <span className={styles.countBadge}>{alerts.length}</span>}
        </div>

        {alerts.length > 0 ? (
          <div className={styles.alertsList}>
            {alerts.map((a) => (
              <AlertCard
                key={`${a.type}:${a.vagaKey ?? "station"}`}
                alert={a}
                liveElapsedSeconds={a.elapsedSeconds + Math.max(0, (now - receivedAt) / 1000)}
                t={t}
              />
            ))}
          </div>
        ) : hasReceived ? (
          <div className={styles.emptyAlert}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>check_circle</span>
            <div>
              <div className={styles.emptyAlertTitle}>{t("noAlertsTitle")}</div>
              <div className={styles.emptyAlertSub}>{t("noAlertsSub")}</div>
            </div>
          </div>
        ) : status === "disconnected" ? (
          <div className={styles.offlineAlert}>
            <span className={`material-symbols-outlined ${styles.offlineIcon}`}>sync_problem</span>
            <div>
              <div className={styles.offlineTitle}>{t("alertsOfflineTitle")}</div>
              <div className={styles.offlineSub}>{t("alertsOfflineSub")}</div>
            </div>
          </div>
        ) : (
          <div className={`${styles.card} ${styles.loading}`}>
            <ProgressSpinner style={{ width: 34, height: 34 }} strokeWidth="6" />
          </div>
        )}
      </section>

      {/* KPIs (starting point para as telas de detalhe) */}
      {kpiLoading && !syn && !acc && !par ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="6" />
        </div>
      ) : (
        <div className={styles.kpiGrid}>
          {/* Estado ao vivo da estação */}
          <DashCard
            to="/reports/synoptic"
            icon="warehouse"
            accent={accents.station}
            title={t("kStation")}
            value={syn ? `${fmt(syn.occupancy.elevators.occupied)}/${fmt(syn.occupancy.elevators.total)}` : "—"}
            unit={syn ? t("elevatorsLower") : undefined}
            sub={
              syn
                ? `${t("supermarket")} ${fmt(syn.occupancy.supermarket.occupied)}/${fmt(syn.occupancy.supermarket.total)}`
                : undefined
            }
            foot={t("openSynoptic")}
          />

          {/* Produção do dia */}
          <DashCard
            to="/reports/hourly-production"
            icon="inventory_2"
            accent={accents.day}
            title={t("kDay")}
            value={syn ? fmt(syn.daily.producedCount) : "—"}
            sub={
              syn
                ? `${t("target")} ${fmt(syn.daily.target, 0)} · ${fmt(dailyAttain, 0)}% · ${t("lastHour")} ${fmt(syn.hourly.producedCount)}`
                : undefined
            }
            foot={t("openHourly")}
          />

          {/* Ciclo médio */}
          <DashCard
            to="/reports/preparation-time"
            icon="timer"
            accent={accents.cycle}
            title={t("kCycle")}
            value={cycleMin != null ? fmt(cycleMin, 1) : "—"}
            unit={cycleMin != null ? t("unitMin") : undefined}
            sub={`${t("vsStd")} ${fmt(stdMin, 1)} ${t("unitMin")}`}
            foot={t("openPrep")}
          />

          {/* OEE — Produção */}
          <DashCard
            to="/reports/oee-performance"
            icon="speed"
            accent={accents.oee}
            title={t("kOee")}
            value={syn ? `${fmt(Math.min(100, dailyAttain), 0)}%` : "—"}
            sub={syn ? `${fmt(syn.daily.producedCount)} / ${fmt(syn.daily.target, 0)} ${t("ofTarget")}` : undefined}
            foot={t("openOee")}
          />

          {/* Projeção do mês */}
          <DashCard
            to="/reports/accumulated-production"
            icon="trending_up"
            accent={accents.month}
            title={t("kMonth")}
            value={acc ? fmt(acc.month.projectionAvailable ? acc.month.projectedClose : acc.month.realized) : "—"}
            sub={
              acc ? (
                acc.month.projectionAvailable ? (
                  `${t("target")} ${fmt(acc.month.target, 0)} · ${t("basisDays", { n: acc.month.projectionBasisDays })}`
                ) : (
                  <span className={styles.warnLine}>
                    <span className="material-symbols-outlined">info</span>
                    {t("projNa")}
                  </span>
                )
              ) : undefined
            }
            foot={t("openAccum")}
          />

          {/* Mini-Pareto do dia */}
          <DashCard
            to="/reports/state-pareto"
            icon="bar_chart"
            accent={accents.pareto}
            title={t("kPareto")}
            value={paretoLoss ? fmt(paretoLoss.minutes) : "—"}
            unit={paretoLoss ? t("unitMin") : undefined}
            sub={paretoLoss ? stateLabel(paretoLoss.state) : t("noLossToday")}
            foot={t("openPareto")}
          />
        </div>
      )}

      <div className={styles.footerNote}>{t("realtimeNote")}</div>
    </div>
  );
};

export default ReportDashboard;
